import { PER_PAGE_UNPAGINATED } from "PFApp/constants/unpaginated";
import { useTemplate } from "PFCore/helpers/templates";
import { useShortlistsInvalidate } from "PFCore/hooks/queries/shortlists/use_shortlists_invalidate";
import { fetchProfiles } from "PFCore/services/profile";
import { closeVacancy, fetchVacancies, reopenVacancy } from "PFCore/services/vacancies";
import { useEffect, useState } from "react";

import { activityContextPlaceholder } from "./activity_page_context";

const emptyVacanciesProfiles = { entries: [], meta: { total: 0 } };

const useActivityPageVacancies = (task) => {
  const [vacancies, setVacancies] = useState(activityContextPlaceholder.vacancies);
  const [vacanciesProfiles, setVacanciesProfiles] = useState(emptyVacanciesProfiles);
  const [vacanciesFilledProfiles, setVacanciesFilledProfiles] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  const template = useTemplate(task.template_id);
  const { invalidateShortlists, invalidateRankedShortlists } = useShortlistsInvalidate();

  const isRole = ["role", "audit_role"].includes(template?.key);
  const canSeeVacancies = task.coowned && isRole;

  const fetchRolesVacanciesPage = (page = 1, perPage = 5) => {
    setLoading(true);
    return fetchVacancies({
      activityId: task.id,
      page: page,
      perPage: perPage
    }).then((resp) => {
      setVacancies(resp);

      const filledProfiles = resp.entries.filter((item) => item.state !== "opened");
      setVacanciesFilledProfiles(filledProfiles);
      const filledProfilesIds = filledProfiles.filter((item) => item.profileId).map((item) => item.profileId);

      if (filledProfilesIds.length > 0) {
        fetchProfiles({
          filters: {
            fields: { id: filledProfilesIds }
          },
          per_page: PER_PAGE_UNPAGINATED
        }).then(setVacanciesProfiles);
      } else {
        setVacanciesProfiles(emptyVacanciesProfiles);
      }
      setLoaded(true);
      setLoading(false);
    });
  };

  const closeVacancies = (selectedReasons, closureDetails) =>
    Promise.allSettled(
      Object.keys(selectedReasons).map((vacancyId) => {
        const reason = selectedReasons[vacancyId];
        if (reason) {
          const isProfileId = reason === "filled_with_named_resource";
          const details = closureDetails[vacancyId];
          const detailsKey = isProfileId ? "profileId" : "closureDetails";

          return closeVacancy({
            activityId: task.id,
            vacancyId,
            closureReason: reason,
            [detailsKey]: details
          }).then(() => {
            invalidateShortlists(task.id);
            invalidateRankedShortlists(task.id);
          });
        }
      })
    );

  const reopenVacancies = (vacancyId) =>
    reopenVacancy({
      activityId: task.id,
      vacancyId
    }).then(() => {
      invalidateShortlists(task.id);
      invalidateRankedShortlists(task.id);
    });

  useEffect(() => {
    if (!canSeeVacancies) {
      return;
    }

    fetchRolesVacanciesPage();
  }, [task.id, task.coowned]);

  const existsOpenVacancy = vacancies.entries.some((vacancy) => vacancy.state === "opened");

  return {
    vacancies,
    vacanciesProfiles,
    vacanciesFilledProfiles,
    fetchRolesVacanciesPage,
    closeVacancies,
    reopenVacancies,
    loaded,
    loading,
    existsOpenVacancy
  };
};

export default useActivityPageVacancies;
